import { useEffect, useMemo, useState } from 'react';
import { Button, Header, Icon, Message, Segment } from 'semantic-ui-react';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { apiRequest, authEnabled, loginRequest, msalConfig, samlEnabled } from './authConfig.js';
import { AuthContext } from './AuthContext.js';
import { setTokenProvider } from '../api.js';

const msalInstance = authEnabled ? new PublicClientApplication(msalConfig) : null;

function SignIn() {
  const { instance, inProgress } = useMsal();
  const busy = inProgress !== 'none';
  return (
    <main className="signin">
      <Segment placeholder textAlign="center">
        <Header icon as="h1"><Icon name="chart bar outline" />Settlement Report Studio
          <Header.Subheader>Sign in with your Microsoft work account to continue.</Header.Subheader>
        </Header>
        <Button primary size="large" type="button" icon="microsoft" labelPosition="left" content="Sign in with Microsoft"
          loading={busy} disabled={busy} onClick={() => instance.loginRedirect(loginRequest)} />
      </Segment>
    </main>
  );
}

/* Signed-in area: supplies the account to the app and a bearer-token source to the API layer. */
function Session({ children }) {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() || accounts[0];

  useEffect(() => {
    setTokenProvider(async () => {
      try {
        return (await instance.acquireTokenSilent({ ...apiRequest, account })).accessToken;
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) { await instance.acquireTokenRedirect({ ...apiRequest, account }); return null; }
        throw e;
      }
    });
    return () => setTokenProvider(null);
  }, [instance, account]);

  const value = useMemo(() => ({
    name: account?.name || account?.username || '',
    username: account?.username || '',
    signOut: () => instance.logoutRedirect({ account }),
  }), [instance, account]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function Gate({ children }) {
  const { instance, inProgress } = useMsal();
  const authed = useIsAuthenticated();
  useEffect(() => { if (!instance.getActiveAccount()) { const [a] = instance.getAllAccounts(); if (a) instance.setActiveAccount(a); } }, [instance, authed]);
  // while the redirect response is being processed show nothing rather than flashing the sign-in screen
  if (inProgress === 'startup' || inProgress === 'handleRedirect') return <Message info className="signin-wait" role="status">Signing in…</Message>;
  return (
    <>
      <AuthenticatedTemplate><Session>{children}</Session></AuthenticatedTemplate>
      <UnauthenticatedTemplate><SignIn /></UnauthenticatedTemplate>
    </>
  );
}

/* SAML: ask the backend who is signed in; if nobody, go to the IdP through /saml/login and come back to this page. */
function SamlGate({ children }) {
  const [state, setState] = useState({ phase: 'checking', user: null, error: null });
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
        if (res.ok) { const me = await res.json(); if (live) setState({ phase: 'in', user: me, error: null }); return; }
        if (res.status === 401) {
          if (live) setState({ phase: 'redirecting', user: null, error: null });
          window.location.assign(`/saml/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
          return;
        }
        throw new Error(`The sign-in service answered ${res.status}`);
      } catch (e) { if (live) setState({ phase: 'error', user: null, error: e.message }); }
    })();
    return () => { live = false; };
  }, []);
  const value = useMemo(() => (state.user ? {
    name: state.user.name || state.user.email,
    username: state.user.email || state.user.nameId,
    signOut: () => window.location.assign('/saml/logout'),
  } : null), [state.user]);
  if (state.phase === 'in') return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  if (state.phase === 'error') {
    return (
      <main className="signin">
        <Message negative role="alert">Could not reach the sign-in service. {state.error}</Message>
        <Button type="button" primary content="Try again" onClick={() => window.location.reload()} />
      </main>
    );
  }
  return <Message info className="signin-wait" role="status">Signing in…</Message>;
}

export default function AuthGate({ children }) {
  if (samlEnabled) return <SamlGate>{children}</SamlGate>;
  if (!authEnabled) return children;
  return <MsalProvider instance={msalInstance}><Gate>{children}</Gate></MsalProvider>;
}
